import axios from "axios";

// In normal operation the build-time REACT_APP_BACKEND_URL points to the same
// host the frontend is served from. When a custom domain is linked to a
// previously-built bundle (e.g. `facturas.viajadverdad.com` while the bundle
// was built for `*.emergent.host`), the baked-in URL no longer matches the
// browser origin and the API calls fail with cross-origin / Network Error.
//
// Strategy: prefer REACT_APP_BACKEND_URL, but if the user is browsing a
// different host (custom domain), fall back to the current `window.origin`
// so requests hit `<custom-domain>/api/*` which the platform ingress
// already routes to the backend pod.
const ENV_BACKEND = process.env.REACT_APP_BACKEND_URL;
const resolvedBackend = (() => {
  if (typeof window === "undefined" || !ENV_BACKEND) return ENV_BACKEND;
  try {
    const envHost = new URL(ENV_BACKEND).hostname;
    if (envHost !== window.location.hostname) return window.location.origin;
  } catch { /* malformed env value */ }
  return ENV_BACKEND;
})();

const BACKEND_URL = resolvedBackend;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API, withCredentials: true });

// Auth helpers
export const exchangeSessionId = async (sessionId) => {
  const res = await api.post("/auth/session", null, { headers: { "X-Session-ID": sessionId } });
  return res.data;
};
export const fetchMe = async () => {
  const res = await api.get("/auth/me");
  return res.data;
};
export const logout = async () => {
  const res = await api.post("/auth/logout");
  return res.data;
};

export const fetchSettings = () => api.get("/settings").then((r) => r.data);
export const updateSettings = (data) => api.put("/settings", data).then((r) => r.data);

export const listInvoices = () => api.get("/invoices").then((r) => r.data);
export const getInvoice = (id) => api.get(`/invoices/${id}`).then((r) => r.data);
export const createInvoice = (data) => api.post("/invoices", data).then((r) => r.data);
export const updateInvoice = (id, data) => api.put(`/invoices/${id}`, data).then((r) => r.data);
export const deleteInvoice = (id) => api.delete(`/invoices/${id}`).then((r) => r.data);
export const getNextNumber = (year) =>
  api.get(`/invoices/next-number${year ? `?year=${year}` : ""}`).then((r) => r.data);

export const downloadInvoicePdf = async (id, number) => {
  const res = await api.get(`/invoices/${id}/pdf`, { responseType: "blob" });
  const blob = new Blob([res.data], { type: "application/pdf" });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `Factura_${(number || id).replace(/\//g, "_")}.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

export const parseExcel = async (file) => {
  const fd = new FormData();
  fd.append("file", file);
  const res = await api.post("/invoices/parse-excel", fd, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return res.data;
};

export const importBulkXlsx = async (file, { dryRun = false } = {}) => {
  const fd = new FormData();
  fd.append("file", file);
  const res = await api.post(`/invoices/import-bulk-xlsx?dry_run=${dryRun}`, fd, {
    headers: { "Content-Type": "multipart/form-data" },
    timeout: 120_000,
  });
  return res.data;
};

export const previewGestionSync = async (invoiceId, { tripIds = [] } = {}) => {
  const res = await api.post(`/invoices/${invoiceId}/sync-gestion`, {
    trip_ids: tripIds,
    confirm: false,
  });
  return res.data;
};

export const confirmGestionSync = async (invoiceId, { tripIds = [] } = {}) => {
  // Now kicks off a BACKGROUND job and returns {job_id, total, type}
  const res = await api.post(
    `/invoices/${invoiceId}/sync-gestion`,
    { trip_ids: tripIds, confirm: true },
    { timeout: 30_000 },
  );
  return res.data;
};

export const getSyncJob = async (jobId) => {
  const res = await api.get(`/sync-jobs/${jobId}`, { timeout: 15_000 });
  return res.data;
};

export const generateAdvanceDraft = async ({ dateFrom, dateTo, sendEmail = true } = {}) => {
  const res = await api.post("/invoices/generate-advance-draft",
    { date_from: dateFrom, date_to: dateTo, send_email: sendEmail });
  return res.data;  // { job_id, status, kind }
};

export const generateFinalDraft = async ({ dateFrom, dateTo, sendEmail = true } = {}) => {
  const res = await api.post("/invoices/generate-final-draft",
    { date_from: dateFrom, date_to: dateTo, send_email: sendEmail });
  return res.data;  // { job_id, status, kind }
};

export const getDraftJob = (jobId) => api.get(`/draft-jobs/${jobId}`).then((r) => r.data);

// Manual / Documentación
export const listManual = () => api.get("/manual").then((r) => r.data);
export const getManualSection = (slug) => api.get(`/manual/${slug}`).then((r) => r.data);
export const updateManualSection = (slug, data) => api.put(`/manual/${slug}`, data).then((r) => r.data);
export const createManualSection = (data) => api.post("/manual", data).then((r) => r.data);
export const deleteManualSection = (slug) => api.delete(`/manual/${slug}`).then((r) => r.data);

// Trip search across invoices
export const searchTripsByLead = (q) =>
  api.get("/trips/search", { params: { q } }).then((r) => r.data);

// Annual list of invoiced advances by trip start year
export const getAdvancesReport = (params) =>
  api.get("/reports/advances", { params }).then((r) => r.data);

export const downloadAdvancesReport = async (fmt, params) => {
  const res = await api.get("/reports/advances/export", { params: { ...params, fmt }, responseType: "blob" });
  const name = `Adelantos_viajes_${params.trip_year}${params.issue_year ? `_facturados_${params.issue_year}` : ""}.${fmt}`;
  const url = window.URL.createObjectURL(new Blob([res.data], { type: res.headers["content-type"] }));
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

// Cost control module (VdV ↔ ET shared expenses)
export const getCostControlYear = (year) =>
  api.get(`/cost-control/${year}`).then((r) => r.data);

export const updateCostControlMonth = (year, month, data) =>
  api.put(`/cost-control/${year}/${month}`, data).then((r) => r.data);

export const syncCostControlFromSheets = (year) =>
  api.post(`/cost-control/${year}/sync`).then((r) => r.data);

export const getCostControlSheetPreview = (year) =>
  api.get(`/cost-control/${year}/sheet-preview`).then((r) => r.data);

// Adjustments (Price discrepancies from accounting emails)
export const listAdjustments = (onlyPending = false) =>
  api.get("/adjustments", { params: { only_pending: onlyPending } }).then((r) => r.data);

export const createAdjustment = (data) =>
  api.post("/adjustments", data).then((r) => r.data);

export const updateAdjustment = (id, data) =>
  api.put(`/adjustments/${id}`, data).then((r) => r.data);

export const deleteAdjustment = (id) =>
  api.delete(`/adjustments/${id}`).then((r) => r.data);

export const parseAdjustmentsFiles = (files) => {
  const fd = new FormData();
  files.forEach((f) => fd.append("files", f));
  return api.post("/adjustments/parse-pdf", fd, {
    headers: { "Content-Type": "multipart/form-data" },
  }).then((r) => r.data);
};

export const applyAdjustmentsToInvoice = (invoiceId, addLine = true) =>
  api.post("/adjustments/apply", { invoice_id: invoiceId, add_line: addLine }).then((r) => r.data);

// ----- Cerrado → Terminado (Susana emails) -----

export const parseTerminadoPdf = (file) => {
  const fd = new FormData();
  fd.append("file", file);
  return api.post("/terminado/parse-pdf", fd, {
    headers: { "Content-Type": "multipart/form-data" },
  }).then((r) => r.data);
};

export const parseTerminadoText = (text) =>
  api.post("/terminado/parse-text", { text }).then((r) => r.data);

export const createTerminadoBatch = (payload) =>
  api.post("/terminado/batches", payload).then((r) => r.data);

export const listTerminadoBatches = () =>
  api.get("/terminado/batches").then((r) => r.data);

export const getTerminadoBatch = (batchId) =>
  api.get(`/terminado/batches/${batchId}`).then((r) => r.data);

export const deleteTerminadoBatch = (batchId) =>
  api.delete(`/terminado/batches/${batchId}`).then((r) => r.data);

export const updateTerminadoName = (batchId, idx, payload) =>
  api.patch(`/terminado/batches/${batchId}/names/${idx}`, payload).then((r) => r.data);

export const listTerminadoPending = () =>
  api.get("/terminado/pending").then((r) => r.data);

export const reconcileTerminadoInvoice = (invoiceId) =>
  api.post(`/terminado/reconcile-invoice/${invoiceId}`).then((r) => r.data);

export const applyTerminadoBatchToCrm = (batchId) =>
  api.post(`/terminado/batches/${batchId}/apply-crm`).then((r) => r.data);

export const forceApplyTerminadoName = (batchId, idx) =>
  api.post(`/terminado/batches/${batchId}/names/${idx}/force-apply`).then((r) => r.data);

export const ignoreTerminadoName = (batchId, idx, reason = null) =>
  api.post(`/terminado/batches/${batchId}/names/${idx}/ignore`, { reason }).then((r) => r.data);

export const unignoreTerminadoName = (batchId, idx) =>
  api.delete(`/terminado/batches/${batchId}/names/${idx}/ignore`).then((r) => r.data);

export const getSyncJobStatus = (jobId) =>
  api.get(`/sync-jobs/${jobId}`).then((r) => r.data);

export const markSyncResultManual = (invoiceId, tripIdLocal, note = null) =>
  api.post(`/invoices/${invoiceId}/sync-results/${tripIdLocal}/mark-manual`, { note }).then((r) => r.data);

export const unmarkSyncResultManual = (invoiceId, tripIdLocal) =>
  api.delete(`/invoices/${invoiceId}/sync-results/${tripIdLocal}/mark-manual`).then((r) => r.data);

export const cancelSyncJob = async (jobId) => {
  const res = await api.delete(`/sync-jobs/${jobId}`);
  return res.data;
};
